import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Lock, RotateCcw, Building2, PlayCircle, Calendar, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

function fmt(v, d=2) { return Number(v||0).toLocaleString(undefined,{minimumFractionDigits:d,maximumFractionDigits:d}); }

// -----------------------------------------------------------------------
//  Admin: Staking overview
// -----------------------------------------------------------------------
export function AdminStaking() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [cancelId, setCancelId] = useState(null);
  const [refund, setRefund] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/admin/stakings${status ? "?status="+status : ""}`); setRows(data.stakings || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const doCancel = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/staking/cancel", { stake_id: cancelId, refund_principal: refund });
      toast.success(data.message);
      setCancelId(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const totals = rows.reduce((acc, r) => {
    if (r.status === "active") { acc.active += 1; acc.principal += Number(r.principal||0); acc.reward += Number(r.reward||0); }
    if (r.status === "completed") { acc.completed += 1; acc.paid += Number(r.total_return||0); }
    return acc;
  }, {active:0, principal:0, reward:0, completed:0, paid:0});

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Admin · Long-Term Staking</div>
          <h1 className="font-heading font-bold text-3xl text-white mt-1"><Lock size={22} className="inline mr-2 -mt-1"/>Staking Overview</h1>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-slate-400">Filter:</label>
          <select data-testid="staking-filter" value={status} onChange={e=>setStatus(e.target.value)} className="nb-input py-1 text-sm">
            <option value="">All</option><option value="active">Active</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Active stakes"       value={totals.active}                color="#00E5FF"/>
        <Stat label="Locked principal"    value={`$${fmt(totals.principal)}`}  color="#8AE234"/>
        <Stat label="Pending rewards"     value={`$${fmt(totals.reward)}`}     color="#EAB308"/>
        <Stat label="Completed & paid"    value={`$${fmt(totals.paid)}`}       color="#A855F7"/>
      </div>

      <div className="nb-card p-4 overflow-x-auto" data-testid="admin-staking-table">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-right px-3 py-2">Principal</th>
              <th className="text-right px-3 py-2">Mult</th>
              <th className="text-right px-3 py-2">Total Return</th>
              <th className="text-left px-3 py-2">Staked</th>
              <th className="text-left px-3 py-2">Unlocks</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {rows.map(r => (
              <tr key={r.id} className="text-slate-200 hover:bg-white/5">
                <td className="px-3 py-2"><div className="text-white text-xs">{r.user_name}</div><div className="text-[10px] text-slate-500 font-mono">{r.user_email}</div></td>
                <td className="px-3 py-2 text-xs">Plan {r.plan_id} · {r.plan_name}</td>
                <td className="px-3 py-2 text-right font-mono">${fmt(r.principal)}</td>
                <td className="px-3 py-2 text-right font-mono text-nb-cyan">{r.multiplier}×</td>
                <td className="px-3 py-2 text-right font-mono text-[#8AE234]">${fmt(r.total_return)}</td>
                <td className="px-3 py-2 text-xs">{new Date(r.staked_at).toLocaleDateString()}</td>
                <td className="px-3 py-2 text-xs">{new Date(r.unlocks_at).toLocaleDateString()}</td>
                <td className="px-3 py-2"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${r.status==="active"?"bg-nb-cyan/15 text-nb-cyan":r.status==="completed"?"bg-[#8AE234]/15 text-[#8AE234]":"bg-red-500/15 text-red-400"}`}>{r.status}</span></td>
                <td className="px-3 py-2 text-right">
                  {r.status==="active" && <button data-testid={`cancel-${r.id}`} onClick={()=>{setCancelId(r.id); setRefund(true);}} className="text-xs px-2 py-1 rounded border border-red-500/30 text-red-400 hover:bg-red-500/10"><RotateCcw size={10} className="inline mr-1"/>Cancel</button>}
                </td>
              </tr>
            ))}
            {rows.length===0 && <tr><td colSpan="9" className="text-center py-8 text-slate-500 text-sm">No stakings yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!cancelId} onOpenChange={(o)=>!o && setCancelId(null)}>
        <DialogContent className="bg-[#0F1626] border-white/10 text-white">
          <DialogHeader><DialogTitle>Cancel Stake</DialogTitle><DialogDescription className="text-slate-400">This ends the stake early. Choose whether to refund the principal to the user's wallet.</DialogDescription></DialogHeader>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={refund} onChange={e=>setRefund(e.target.checked)} data-testid="cancel-refund"/>
            Refund principal back to user's balance
          </label>
          <DialogFooter>
            <button onClick={()=>setCancelId(null)} className="nb-btn-secondary text-sm">Close</button>
            <button onClick={doCancel} disabled={busy} data-testid="confirm-cancel" className="nb-btn-primary text-sm bg-red-500 text-white hover:opacity-90">{busy?"…":"Confirm cancel"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

// -----------------------------------------------------------------------
//  Admin: Shareholder payouts
// -----------------------------------------------------------------------
export function AdminShareholder() {
  const [payouts, setPayouts] = useState([]);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth()-1);
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}`;
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/admin/shareholder/payouts${month?"?month="+month:""}`); setPayouts(data.payouts || []); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/shareholder/run-payouts", { month }); toast.success(data.message); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const total = payouts.reduce((s, r) => s + Number(r.bonus||0), 0);

  return (
    <AppLayout variant="admin" requireAdmin>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-nb-cyan">Admin · Company Shareholder Program</div>
          <h1 className="font-heading font-bold text-3xl text-white mt-1"><Building2 size={22} className="inline mr-2 -mt-1"/>Shareholder Payouts</h1>
        </div>
      </div>

      <div className="nb-card p-5 mb-6">
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400">Month (YYYY-MM)</label>
            <input data-testid="payout-month" type="month" value={month} onChange={e=>setMonth(e.target.value)} className="nb-input mt-1"/>
            <div className="text-[10px] text-slate-500 mt-1">The system runs the previous month's payout automatically on the 1st. Use this to trigger any month on demand (idempotent — already-paid users are skipped).</div>
          </div>
          <div className="md:col-span-2 flex md:justify-end">
            <button onClick={run} disabled={busy} data-testid="run-payouts" className="px-6 py-3 rounded-xl bg-nb-cyan text-black font-heading font-bold hover:opacity-90 disabled:opacity-50">
              <PlayCircle size={16} className="inline mr-1 -mt-0.5"/>{busy?"Running…":`Run Shareholder Payouts for ${month}`}
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        <Stat label="Members credited"    value={payouts.length}         color="#00E5FF"/>
        <Stat label="Total bonus paid"    value={`$${fmt(total)}`}       color="#8AE234"/>
        <Stat label="Payout month"        value={month || "-"}           color="#A855F7"/>
      </div>

      <div className="nb-card p-4 overflow-x-auto" data-testid="admin-shareholder-table">
        <table className="w-full text-sm">
          <thead className="text-xs text-slate-500 uppercase tracking-widest">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-right px-3 py-2">Min Bal</th>
              <th className="text-right px-3 py-2">Team Vol</th>
              <th className="text-right px-3 py-2">Base</th>
              <th className="text-right px-3 py-2">Rate</th>
              <th className="text-right px-3 py-2">Bonus</th>
              <th className="text-left px-3 py-2">Credited At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {payouts.map(p => (
              <tr key={p.id} className="text-slate-200 hover:bg-white/5">
                <td className="px-3 py-2"><div className="text-white text-xs">{p.user_name}</div><div className="text-[10px] text-slate-500 font-mono">{p.user_email}</div></td>
                <td className="px-3 py-2 text-xs">Plan {p.plan_id} · {p.plan_name}</td>
                <td className="px-3 py-2 text-right font-mono">${fmt(p.min_balance_snapshot)}</td>
                <td className="px-3 py-2 text-right font-mono">${fmt(p.team_volume)}</td>
                <td className="px-3 py-2 text-right font-mono">${fmt(p.base)}</td>
                <td className="px-3 py-2 text-right font-mono text-nb-cyan">{p.bonus_pct}%</td>
                <td className="px-3 py-2 text-right font-mono text-[#8AE234] font-bold">+${fmt(p.bonus)}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500 font-mono">{new Date(p.credited_at).toLocaleString()}</td>
              </tr>
            ))}
            {payouts.length===0 && <tr><td colSpan="8" className="text-center py-8 text-slate-500 text-sm">No payouts for this month yet. Click "Run Shareholder Payouts" to trigger.</td></tr>}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="nb-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-mono font-bold text-xl mt-1" style={{color}}>{value}</div>
    </div>
  );
}
